import { ErrorHandler, Injector, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { ErrorMessage } from '../public/store/+state/stores.actions';
import { WindowRefService } from '../window.service';
import {
  AdminDiagnosticContext,
  GlobalErrorService,
  PublicErrorScreen,
  trimStack,
} from './global-error.service';
import { safeSessionStorage } from '../safe-web-storage';
import { LogService } from '../shared/logging/LogService';
import { WhitelabelService } from '../shared/whitelabel.service';
import { isDynamicImportFailure } from './chunk-load-error.util';
import { isPdfDocumentLoadFailure } from './pdf-load-error.util';
import { mapsModuleLoadFailure } from './maps-module-load-error.util';
import { GoogleMapsLoaderService } from '../shared/google-maps-loader.service';
import { environment } from '../../environments/environment';
import { APPMODE } from '../../environments/appmode.enum';
import {
  resolveIncidentReference,
  UNEXPECTED_ERROR_CODE,
} from './incident-reference.util';

const CHUNK_RELOAD_STORAGE_KEY = 'admin-error:chunkReloadAt';
const CHUNK_RELOAD_COOLDOWN_MS = 10_000;

/**
 * The shortest time an identical customer-side error counts as a repeat of
 * the fault already reported (card #11603). A single start-up fault used to
 * produce 15 to 50 identical errors inside one second, each logging its own
 * entry and minting its own incident reference; every recorded burst finished
 * well inside a second. A repeat writes nothing to the log and shows the
 * reference already reported. Past this window an identical error with no
 * error page up is reported again once the customer has navigated, or once
 * `PUBLIC_ERROR_INCIDENT_MAX_MS` has passed.
 */
export const PUBLIC_ERROR_DEDUPE_WINDOW_MS = 5_000;

/**
 * How long a repeat can still be the page moving on by itself before the
 * customer has navigated (card #11603): a start-up whose cart initialisation
 * finishes late on a slow connection. Past this, an identical error with no
 * error page up is a new occurrence and is reported, so a fault the customer
 * hits again minutes later, without navigating, gets its own log entry and
 * reference instead of showing a stale one.
 */
export const PUBLIC_ERROR_INCIDENT_MAX_MS = 60_000;

/**
 * How many distinct customer-side error signatures stay remembered. A single
 * slot would only collapse a strictly self-repeating burst: two faults in
 * alternation (A, B, A, B — the start-up throw plus an error raised by the
 * error state itself) would never match the immediately preceding signature and
 * would storm exactly as before. A handful of slots covers the interleaved case
 * at no meaningful cost; the oldest reported signature is evicted past it, so
 * the map cannot grow with the visit.
 */
export const PUBLIC_ERROR_SIGNATURE_LIMIT = 10;

/** A customer-side error the handler has logged and raised a screen for. */
interface ReportedError {
  /** When it was reported, on the monotonic `performance.now()` clock. */
  at: number;
  /** The reference its error screen shows. */
  reference: string;
  /** `navigationsStarted` when it was reported. */
  navigations: number;
}

/** `undefined` unless the value really is a string, for the log contexts. */
const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

/** `undefined` unless the value really is a number, for the log contexts. */
const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' ? value : undefined;

/**
 * What the customer side logs for an unexpected error. Mirrors
 * `AdminDiagnosticContext`, minus the fields that only exist on the admin side,
 * and is deliberately free of anything the customer typed.
 */
export interface PublicDiagnosticContext {
  reference: string;
  url?: string;
  status?: number;
  name?: string;
  message?: string;
  stack?: string;
  route?: string;
  /**
   * The store the customer was on. The error screen used to log a second
   * `customer ui error` entry just to carry these; one incident now writes
   * one entry (card #11603). A start-up error can come before the store has
   * loaded, so `host` (the store's own address) names it in every entry.
   */
  host?: string;
  storeId?: number;
  storeAlias?: string;
  timestamp: string;
}

@Injectable()
export class CustomErrorHandler implements ErrorHandler {
  /**
   * Customer-side error signatures we have reported, newest last. Capped at
   * `PUBLIC_ERROR_SIGNATURE_LIMIT`.
   */
  private readonly recentPublicErrors = new Map<string, ReportedError>();

  /**
   * Navigations started since the first customer-side error. A repeat seen
   * before the customer navigates is the page moving on by itself (cart
   * initialisation finishing, a deferred render) — on a slow connection that
   * can take longer than the window — not a new occurrence (card #11603). See
   * `PUBLIC_ERROR_INCIDENT_MAX_MS` for how long that holds.
   */
  private navigationsStarted = 0;
  private watchingNavigations = false;

  /** Maps modules already logged as failed on this page load (#11887). */
  private readonly loggedMapsModuleFailures = new Set<string>();

  constructor(
    private injector: Injector,
    private whitelabelService: WhitelabelService,
  ) {}

  handleError(error) {
    // 401 must run before the chunk-load branch so an auth failure wrapped in
    // a chunk-style message still goes through the auth reload path.
    const unauthorizedMessage = /401 Unauthorized/i;
    if (unauthorizedMessage.test(error?.message)) {
      this.performReload();
      return;
    }

    if (isDynamicImportFailure(error)) {
      if (this.tryChunkReload()) {
        return;
      }
      // Cooldown active — fall through to the diagnostic page so the user
      // can read the failure details instead of looping.
    }

    // The brochure page already shows its own retry state for these; this is
    // the viewer library's uncaught copy of the same failure.
    if (isPdfDocumentLoadFailure(error)) {
      return;
    }

    // A Google Maps module the SDK fetches on first use could not be loaded.
    // The map consumers show their own failed states for it (story #11887).
    const failedMapsModule = mapsModuleLoadFailure(error);
    if (failedMapsModule) {
      this.handleMapsModuleLoadFailure(failedMapsModule);
      return;
    }

    const geoCodeNoResultMessage =
      /MapsRequestError: GEOCODER_GEOCODE: ZERO_RESULTS:/;
    if (geoCodeNoResultMessage.test(error?.message)) {
      return;
    }

    // "NG0100: ExpressionChangedAfterItHasBeenCheckedError: Expression has changed after it was checked.
    // Return to not create a loop of errors
    const ng0100Message = /NG0100/;
    if (ng0100Message.test(error?.message)) {
      return;
    }

    const errorService = this.injector.get(GlobalErrorService);
    const windowRefSer = this.injector.get(WindowRefService);
    const logService = this.injector.get(LogService);

    if (
      windowRefSer.nativeWindowLocation.hostname.startsWith('admin') ||
      this.whitelabelService.isWhiteLabelDomainByHostName(
        windowRefSer.nativeWindowLocation.hostname,
      )
    ) {
      // Admin side
      logService.error('error', error?.stack || error);
      errorService.currentAngularError = error?.stack || error;
      errorService.setDiagnosticContext(
        this.buildAdminDiagnosticContext(error),
      );
      // For all unhandled errors in admin side go to error page
      errorService.navigateToErrorPage(['/manager/expectederror']);
    } else {
      // Public side — drop a repeat first, then build reference, log sanitized
      // context, dispatch error action. The repeat check runs before
      // `resolveIncidentReference` so a suppressed repeat does not even mint a
      // reference nobody will ever see.
      this.watchNavigations();
      const route = this.resolveRoute();
      const signature = this.publicErrorSignature(error, route);
      const screen = errorService.readPublicErrorScreen();
      const seen = this.collapsesRepeats()
        ? this.recentPublicErrors.get(signature)
        : undefined;
      if (seen && this.isRepeat(seen, screen)) {
        // Background work (cart initialisation, a deferred render) can replace
        // the error page while the fault keeps firing, which would leave the
        // customer on a menu whose start-up just failed. Put the same screen
        // back, with the same reference and no new log entry. A fault that
        // fires once still gives way to the menu, as it always has.
        if (!screen.errorPageShowing) {
          errorService.dispatchErrorAction(
            this.unexpectedErrorAction(seen.reference),
          );
        }
        return;
      }
      const reference = resolveIncidentReference(error);
      this.recordReported(signature, reference);
      logService.error(
        'customer ui error',
        this.buildPublicDiagnosticContext(
          error,
          reference,
          route,
          screen,
          asString(windowRefSer.nativeWindowLocation?.hostname),
        ),
      );
      errorService.dispatchErrorAction(this.unexpectedErrorAction(reference));
    }
  }

  /**
   * Tells the loader, so the location dialog and the address lookups switch
   * to their failed states, and logs it once per module per page load under
   * its own message, so these stay countable without counting as Error 206.
   */
  private handleMapsModuleLoadFailure(module: string): void {
    this.injector.get(GoogleMapsLoaderService).reportModuleLoadFailure(module);
    if (this.loggedMapsModuleFailures.has(module)) {
      return;
    }
    this.loggedMapsModuleFailures.add(module);
    const windowRefSer = this.injector.get(WindowRefService);
    const screen = this.injector
      .get(GlobalErrorService)
      .readPublicErrorScreen();
    this.injector.get(LogService).error('maps module load failed', {
      module,
      route: this.resolveRoute(),
      host: asString(windowRefSer.nativeWindowLocation?.hostname),
      storeId: screen.storeId,
    });
  }

  /**
   * An identical error is a repeat of a fault already reported — no log entry,
   * no new reference — while an error page is up, within
   * `PUBLIC_ERROR_DEDUPE_WINDOW_MS` of the report, or before the customer has
   * navigated since, up to `PUBLIC_ERROR_INCIDENT_MAX_MS` (card #11603).
   * Signatures are kept per fault rather than in one slot, so two errors in
   * alternation collapse too. A different error is always reported.
   */
  private isRepeat(seen: ReportedError, screen: PublicErrorScreen): boolean {
    // Monotonic: a device clock set back (NTP, a manual change) must not make
    // a new incident look like a repeat of an old one.
    const age = performance.now() - seen.at;
    return (
      !!screen.errorPageShowing ||
      age < PUBLIC_ERROR_DEDUPE_WINDOW_MS ||
      (this.navigationsStarted === seen.navigations &&
        age < PUBLIC_ERROR_INCIDENT_MAX_MS)
    );
  }

  /**
   * Repeats are collapsed on the customer storefront only (card #11603). The
   * GoAdmin apps run on `localhost`, so their uncaught errors reach the public
   * branch too; there every occurrence is still logged and shown, as before.
   */
  private collapsesRepeats(): boolean {
    return environment.mode !== APPMODE.Admin;
  }

  private watchNavigations(): void {
    if (this.watchingNavigations) {
      return;
    }
    this.watchingNavigations = true;
    this.injector.get(Router).events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.navigationsStarted += 1;
      }
    });
  }

  private unexpectedErrorAction(reference: string): ErrorMessage {
    return new ErrorMessage(
      'public.global.errorUnexpected',
      UNEXPECTED_ERROR_CODE,
      [],
      undefined,
      undefined,
      reference,
      true,
    );
  }

  private recordReported(signature: string, reference: string): void {
    // Re-insert so Map iteration order makes the least recently reported
    // signature the first eviction candidate.
    this.recentPublicErrors.delete(signature);
    this.recentPublicErrors.set(signature, {
      at: performance.now(),
      reference,
      navigations: this.navigationsStarted,
    });
    while (this.recentPublicErrors.size > PUBLIC_ERROR_SIGNATURE_LIMIT) {
      const oldest = this.recentPublicErrors.keys().next().value;
      this.recentPublicErrors.delete(oldest);
    }
  }

  /**
   * What counts as "the same error" for suppression: name + message + route,
   * as the story defines it (card #11603). An `HttpErrorResponse` message
   * already carries the request's url and status.
   */
  private publicErrorSignature(error: any, route: string | undefined): string {
    return [
      asString(error?.name) ?? '',
      asString(error?.message) ?? '',
      route ?? '',
    ].join('|');
  }

  // Returns true if we performed the reload, false if the cooldown blocked it.
  private tryChunkReload(): boolean {
    const now = Date.now();
    // Card #11603: via the crash-safe helper. When the browser refuses storage
    // the cooldown cannot survive the reload, so we still reload — the
    // alternative is leaving the user on an unrecoverable stale chunk.
    const raw = safeSessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY);
    const lastAt = raw ? Number(raw) : 0;
    if (
      Number.isFinite(lastAt) &&
      lastAt > 0 &&
      now - lastAt < CHUNK_RELOAD_COOLDOWN_MS
    ) {
      return false;
    }
    safeSessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
    this.performReload();
    return true;
  }

  // Indirected for testability — Karma cannot spy on window.location.reload
  // on every browser configuration; spying on this method works everywhere.
  protected performReload(): void {
    window.location.reload();
  }

  /**
   * The route the error happened on, or `undefined` when the router cannot say.
   * Read once per handled error and passed on, so the signature and the logged
   * context can never disagree about where the fault was.
   */
  private resolveRoute(): string | undefined {
    const router = this.injector.get(Router);
    try {
      return router?.url;
    } catch {
      return undefined;
    }
  }

  private buildPublicDiagnosticContext(
    error: any,
    reference: string,
    route: string | undefined,
    screen: PublicErrorScreen,
    host: string | undefined,
  ): PublicDiagnosticContext {
    return {
      reference,
      url: asString(error?.url),
      status: asNumber(error?.status),
      name: asString(error?.name),
      message: asString(error?.message),
      // Card #11603: without the stack, a 206 reference in the production log
      // could only ever be guessed back to a line of code.
      stack: trimStack(error?.stack),
      route,
      host,
      storeId: screen.storeId,
      storeAlias: screen.storeAlias,
      timestamp: new Date().toISOString(),
    };
  }

  private buildAdminDiagnosticContext(error: any): AdminDiagnosticContext {
    return {
      url: asString(error?.url),
      status: asNumber(error?.status),
      name: asString(error?.name),
      message: asString(error?.message),
      stack: asString(error?.stack),
      route: this.resolveRoute(),
      at: new Date().toISOString(),
    };
  }
}
